export type CSSKnownValue =
  string |
  number |
  CSSRule |
  ((property: string) => string) |
  ((index: number) => string) |
  ((property: string, value: string | null, priority?: string) => void);

/** Load a single image from a URL. */
export function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

/** Load multiple images from URLs. */
export function loadImages(urls: string[]): Promise<HTMLImageElement[]> {
  return Promise.all(urls.map(loadImage));
}

export interface Canvas2DCtx {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
}

/** Create an offscreen 2D canvas and its context. */
export function createCanvas2D(width: number, height: number): Canvas2DCtx {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) { throw new Error('2D canvas context not available'); }
  return { canvas, ctx };
}

/** Trigger a download of a Blob. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Trigger a download of JSON data. */
export function downloadJson(data: unknown, filenameBase: string): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const filename = filenameBase.endsWith('.json') ? filenameBase : `${filenameBase}.json`;
  downloadBlob(blob, filename);
}

export function GetKnownElementById(id: string): HTMLElement {
  const elem: HTMLElement | null = document.getElementById(id);
  if (!elem) { throw new Error(`Could not find element with id <${id}>`); }

  return elem;
}

export function GetFirstKnownElementByTagName(name: string): HTMLElement {
  const elems: HTMLCollectionOf<Element> = document.getElementsByTagName(name);
  const elem: Element | null = elems.item(0);
  if (!(elem instanceof HTMLElement)) { throw new Error(`Could not find element with tag name <${name}>`); }

  return elem;
}

export function GetKnownStyle(elem: HTMLElement, styleProp: keyof CSSStyleDeclaration): CSSKnownValue {
  const style: CSSStyleDeclaration | undefined = document.defaultView?.getComputedStyle(elem, null);
  if (!style) {
    // eslint-disable-next-line no-console
    console.error(`Could not find styles of elem`, elem);
    throw new Error(`Could not find styles`);
  }

  const value = style[styleProp];
  if (!value) { throw new Error(`Value not known for style prop <${String(styleProp)}>`); }

  return value;
}

export function DeepQuerySelector(selector: string): Element | null {
  // First try regular querySelector
  let element = document.querySelector(selector);
  if (element) { return element; }

  // Recursively search through shadow DOM
  function searchShadowDOM(root: Element | ShadowRoot): Element | null {
    const elements = root.querySelectorAll('*');
    for (const elem of Array.from(elements)) {
      // Check if the element itself matches
      if (elem.matches(selector)) { return elem; }

      if (elem.shadowRoot) {
        // First check direct children of shadow root
        element = elem.shadowRoot.querySelector(selector);
        if (element) { return element; }

        // Then recursively search deeper
        const deepResult = searchShadowDOM(elem.shadowRoot);
        if (deepResult) { return deepResult; }
      }
    }
    return null;
  }

  // Start search from document root
  return searchShadowDOM(document.documentElement);
}
